import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { doc, getDoc, updateDoc, increment } from "firebase/firestore";
import { db } from "../Components/Firebase";
import { Container, Row, Col, Button, Badge, Card } from "react-bootstrap";

const Watch = () => {
  const { episodeId } = useParams();
  const [episode, setEpisode] = useState(null);
  const [creator, setCreator] = useState(null);
  const [loading, setLoading] = useState(true);
  const [viewTracked, setViewTracked] = useState(false);

  useEffect(() => {
    const fetchEpisodeAndCreator = async () => {
      try {
        // Fetch episode data
        const episodeRef = doc(db, "episodes", episodeId);
        const episodeSnapshot = await getDoc(episodeRef);
        
        if (episodeSnapshot.exists()) {
          const episodeData = episodeSnapshot.data();
          setEpisode({ id: episodeSnapshot.id, ...episodeData });

          // Fetch creator data
          const creatorRef = doc(db, "creators", episodeData.creatorId);
          const creatorSnapshot = await getDoc(creatorRef);
          
          if (creatorSnapshot.exists()) {
            setCreator(creatorSnapshot.data());
          }

          // Track view (only once per page load)
          if (!viewTracked) {
            await updateDoc(episodeRef, {
              views: increment(1),
              lastViewed: new Date()
            });
            setViewTracked(true);
          }
        }
      } catch (error) {
        console.error("Error fetching episode:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchEpisodeAndCreator();
  }, [episodeId, viewTracked]);

  if (loading) {
    return (
      <div className="explore-page" style={{ minHeight: "100vh" }}>
        <div className="star-field">
          {Array.from({ length: 100 }).map((_, i) => (
            <div
              key={i}
              className="star"
              style={{
                top: `${Math.random() * 100}%`,
                left: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 3}s`
              }}
            />
          ))}
        </div>
        <Container className="py-5 d-flex justify-content-center align-items-center" style={{ minHeight: "50vh" }}>
          <div className="text-light text-center">
            <div className="spinner-border text-light mb-3" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p>Loading episode...</p>
          </div>
        </Container>
      </div>
    );
  }

  if (!episode) {
    return (
      <div className="explore-page" style={{ minHeight: "100vh" }}>
        <div className="star-field">
          {Array.from({ length: 100 }).map((_, i) => (
            <div
              key={i}
              className="star"
              style={{
                top: `${Math.random() * 100}%`,
                left: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 3}s`
              }}
            />
          ))}
        </div>
        <Container className="py-5 text-center">
          <div className="text-light">
            <h2>Episode Not Found</h2>
            <p>The episode you're looking for doesn't exist or has been removed.</p>
            <Link to="/explore">
              <Button variant="primary">Back to Explore</Button>
            </Link>
          </div>
        </Container>
      </div>
    );
  }

  return (
    <div className="explore-page" style={{ minHeight: "100vh" }}>
      <div className="star-field">
        {Array.from({ length: 100 }).map((_, i) => (
          <div
            key={i}
            className="star"
            style={{
              top: `${Math.random() * 100}%`,
              left: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 3}s`
            }}
          />
        ))}
      </div>

      <Container fluid className="py-4">
        <Row className="justify-content-center">
          {/* Main Content */}
          <Col lg={8} xl={7}>
            {/* Video Player */}
            <div className="mb-4">
              <div 
                style={{ 
                  position: 'relative', 
                  width: '100%', 
                  aspectRatio: '16/9',
                  backgroundColor: '#000',
                  borderRadius: '12px',
                  overflow: 'hidden',
                  boxShadow: '0 8px 32px rgba(0,0,0,0.4)'
                }}
              >
                <iframe
                  title={`Watch ${episode.title}`}
                  src={`https://iframe.videodelivery.net/${episode.videoId}?autoplay=true`}
                  style={{ 
                    width: '100%', 
                    height: '100%', 
                    border: 'none'
                  }}
                  allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture"
                  allowFullScreen
                />
              </div>
            </div>

            {/* Episode Info */}
            <div className="mb-4">
              <div className="d-flex align-items-start justify-content-between mb-3">
                <div className="flex-grow-1">
                  <h1 className="text-light mb-2" style={{ fontSize: '1.5rem', fontWeight: '600' }}>
                    {episode.title}
                  </h1>
                  <div className="d-flex align-items-center flex-wrap gap-3 mb-2">
                    <span className="text-light" style={{ fontSize: '0.95rem', fontWeight: '500' }}>
                      {(episode.views || 0).toLocaleString()} views
                    </span>
                    <span className="text-muted" style={{ fontSize: '0.95rem' }}>•</span>
                    <span className="text-light" style={{ fontSize: '0.95rem', fontWeight: '500' }}>
                      {episode.date?.toDate().toLocaleDateString()}
                    </span>
                    <div className="d-flex align-items-center gap-2 flex-wrap">
                      <Badge 
                        bg="info" 
                        className="px-3 py-1"
                        style={{ 
                          fontSize: '0.85rem',
                          fontWeight: '500',
                          borderRadius: '20px'
                        }}
                      >
                        {episode.genre}
                      </Badge>
                      {episode.trending && (
                        <Badge 
                          bg="danger" 
                          className="px-3 py-1"
                          style={{ 
                            fontSize: '0.85rem',
                            fontWeight: '600',
                            borderRadius: '20px',
                            background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a52 100%)',
                            border: 'none'
                          }}
                        >
                          🔥 Trending
                        </Badge>
                      )}
                      {episode.nsfw && (
                        <Badge 
                          bg="warning" 
                          text="dark" 
                          className="px-3 py-1"
                          style={{ 
                            fontSize: '0.85rem',
                            fontWeight: '600',
                            borderRadius: '20px'
                          }}
                        >
                          NSFW
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Creator Info & Support Section */}
              <Card 
                className="mb-4"
                style={{ 
                  backgroundColor: "#1e1b4b", 
                  border: "1px solid #374151",
                  borderRadius: "12px"
                }}
              >
                <Card.Body className="p-4">
                  <Row className="align-items-center">
                    <Col md={8}>
                      <div className="d-flex align-items-center mb-3">
                        <div 
                          className="rounded-circle bg-gradient me-3 d-flex align-items-center justify-content-center"
                          style={{ 
                            width: '48px', 
                            height: '48px',
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            fontSize: '1.2rem',
                            fontWeight: 'bold',
                            color: 'white'
                          }}
                        >
                          {creator?.username ? creator.username.charAt(0).toUpperCase() : 'C'}
                        </div>
                        <div className="flex-grow-1">
                          <Link 
                            to={`/creator/${episode.creatorId}`}
                            className="text-decoration-none"
                          >
                            <h5 className="text-light mb-1 hover-underline">
                              {creator?.username || 'Creator'}
                              {episode.founderBadge && (
                                <Badge bg="warning" text="dark" className="ms-2 small">
                                  ✨ Founding Creator
                                </Badge>
                              )}
                            </h5>
                          </Link>
                          {creator?.bio && (
                            <p className="text-muted mb-0 small" style={{ fontSize: '0.9rem' }}>
                              {creator.bio}
                            </p>
                          )}
                        </div>
                      </div>
                    </Col>
                    <Col md={4} className="text-md-end">
                      {creator?.patreonUrl && (
                        <div className="d-flex flex-column gap-2">
                          <Button
                            href={creator.patreonUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn-patreon d-flex align-items-center justify-content-center gap-2"
                            style={{
                              background: 'linear-gradient(135deg, #ff6b6b 0%, #ff8e8e 100%)',
                              border: 'none',
                              borderRadius: '8px',
                              padding: '12px 20px',
                              fontWeight: '600',
                              fontSize: '0.95rem',
                              boxShadow: '0 4px 12px rgba(255, 107, 107, 0.3)',
                              transition: 'all 0.2s ease'
                            }}
                            onMouseEnter={(e) => {
                              e.target.style.transform = 'translateY(-2px)';
                              e.target.style.boxShadow = '0 6px 20px rgba(255, 107, 107, 0.4)';
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.transform = 'translateY(0)';
                              e.target.style.boxShadow = '0 4px 12px rgba(255, 107, 107, 0.3)';
                            }}
                          >
                            <span style={{ fontSize: '1.1rem' }}>💖</span>
                            Support on Patreon
                          </Button>
                          <small className="text-muted text-center">
                            Help this creator continue making amazing content!
                          </small>
                        </div>
                      )}
                    </Col>
                  </Row>
                </Card.Body>
              </Card>

              {/* Back to Explore */}
              <div className="mb-4">
                <Link to="/explore" className="text-decoration-none">
                  <Button 
                    variant="outline-light" 
                    className="d-flex align-items-center gap-2"
                    style={{ borderRadius: '8px' }}
                  >
                    <span>←</span>
                    Back to Explore
                  </Button>
                </Link>
              </div>
            </div>
          </Col>

          {/* Sidebar */}
          <Col lg={4} xl={3}>
            <div className="sticky-top" style={{ top: '20px' }}>
              {/* Advertisement Placeholder */}
              <Card 
                className="mb-4"
                style={{ 
                  backgroundColor: "#1e1b4b", 
                  border: "1px solid #374151",
                  borderRadius: "12px"
                }}
              >
                <Card.Body className="p-4 text-center">
                  <p className="text-muted mb-3 small">Advertisement</p>
                  <div 
                    id="carbon-ad"
                    style={{
                      minHeight: '250px',
                      background: 'linear-gradient(135deg, #2d1b69 0%, #1e1b4b 100%)',
                      borderRadius: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: '2px dashed #374151'
                    }}
                  >
                    <div className="text-muted">
                      <div style={{ fontSize: '2rem', marginBottom: '10px' }}>📺</div>
                      <p className="small mb-0">Ad Space</p>
                      <p className="small">Carbon/AdSense</p>
                    </div>
                  </div>
                </Card.Body>
              </Card>

              {/* Creator Quick Info */}
              {creator && (
                <Card 
                  style={{ 
                    backgroundColor: "#1e1b4b", 
                    border: "1px solid #374151",
                    borderRadius: "12px"
                  }}
                >
                  <Card.Body className="p-4">
                    <h6 className="text-light mb-3">About the Creator</h6>
                    <div className="text-center mb-3">
                      <div 
                        className="rounded-circle bg-gradient mx-auto mb-2 d-flex align-items-center justify-content-center"
                        style={{ 
                          width: '60px', 
                          height: '60px',
                          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                          fontSize: '1.5rem',
                          fontWeight: 'bold',
                          color: 'white'
                        }}
                      >
                        {creator.username ? creator.username.charAt(0).toUpperCase() : 'C'}
                      </div>
                      <h6 className="text-light mb-1">{creator.username}</h6>
                      {creator.bio && (
                        <p className="text-muted small mb-3">{creator.bio}</p>
                      )}
                    </div>
                    <Link to={`/creator/${episode.creatorId}`}>
                      <Button 
                        variant="outline-light" 
                        size="sm" 
                        className="w-100"
                        style={{ borderRadius: '8px' }}
                      >
                        View Creator Profile
                      </Button>
                    </Link>
                  </Card.Body>
                </Card>
              )}
            </div>
          </Col>
        </Row>
      </Container>

      <style jsx>{`
        .hover-underline:hover {
          text-decoration: underline !important;
        }
        
        .btn-patreon:hover {
          transform: translateY(-2px) !important;
        }
        
        .star {
          position: absolute;
          width: 2px;
          height: 2px;
          background: white;
          border-radius: 50%;
          animation: twinkle 3s ease-in-out infinite;
        }
        
        @keyframes twinkle {
          0%, 100% { opacity: 0.3; }
          50% { opacity: 1; }
        }
        
        .explore-page {
          background: linear-gradient(135deg, #1e1b4b 0%, #3c366b 100%);
          position: relative;
          overflow: hidden;
        }
        
        .star-field {
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          pointer-events: none;
          z-index: 1;
        }
        
        .container-fluid {
          position: relative;
          z-index: 2;
        }
      `}</style>
    </div>
  );
};

export default Watch;